import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from 'react-router-dom';
import {
    LearnerLayout,
    OrganisationOrTrainerLayout,
    MainLayout,
    AdminLayout,
    PreviewLayout,
    OrganisationLayout,
    PublicLayout,
    InitialLoginLayout,
    ProfileLayout,
    CourseBuilderLayout,
    HomepageLayout,
    LearnerCourseLayout,
} from './layouts/MainLayout';
import { AuthProvider } from './contextProviders/AuthContext';
import Redirect from './pages/Redirect';
import ViewPublishedCoursePage from './pages/trainer/ViewPublishedCoursePage';
import CourseBuilderPage from 'pages/build-course/CourseBuilderPage';
import EnrollPage from './pages/learner/EnrollPage';
import AdminDashboardPage from 'pages/admin/AdminDashboardPage';
import ChangeUserRolePage from 'pages/admin/ChangeUserRolePage';
import TrainerDashboardPage from 'pages/trainer/TrainerDashboardPage';
import CreateOrganisationPage from 'pages/admin/CreateOrganisationPage';
import UpdateOrganisationPage from 'pages/admin/UpdateOrganisationPage';
import NotificationsPage from 'pages/notifications/NotificationsPage';
import { UserDetailsPage } from 'pages/OA/trainers/UserDetailsPage';
import InvitationPage from 'pages/invitation/InvitationPage';
import FileUploadPage from './pages/OA/files/FileUploadPage';
import ScheduleCoursePage from './pages/schedule/ScheduleCoursePage';
import OrganisationOverviewPage from 'pages/OA/OrganisationOverviewPage';
import LiveTrainingDetailPage from './pages/schedule/courses/live-training/LiveTrainingDetailPage';
import LogoUploadPage from './pages/OA/logo/LogoUploadPage';
import BlockAccessIfNotSignedIn from './layouts/components/BlockAccessIfNotSignedIn';
import ProfilePage from './pages/profile/ProfilePage';
import PublicOrganisationPage from 'pages/public/PublicOrganisationPage';
import PublicCoursePage from 'pages/public/PublicCoursePage';
import AddUserPage from './pages/admin/AddUserPage';
import NotifyUsersPage from 'pages/admin/NotifyUsersPage';
import OADashboard, { OADashboardContent } from 'pages/OA/OADashboard';
import InitialLoginPage from 'pages/initial-login/InitialLoginPage';
import LiveCourseDetailPage from './pages/schedule/courses/LiveCourseDetailPage';
import { GlobalStyle } from 'layouts/GlobalStyle';
import Homepage from 'pages/signup/Homepage';
import { SignOut } from 'pages/signup/SignOut';
import LearnerDashboard from 'pages/learner/LearnerCoursesPage';
import LearnerCoursePage from 'pages/learner/LearnerCoursePage';

const router = createBrowserRouter(
    createRoutesFromElements(
        <>
            <Route path="/public" element={<PublicLayout />}>
                <Route path="organisation/:name" element={<PublicOrganisationPage />} />
                <Route path="course/:id" element={<PublicCoursePage />} />
            </Route>

            <Route path="/signin" element={<HomepageLayout />}>
                <Route index element={<Homepage homepageState={'signIn'} />} />
            </Route>
            <Route path="/signup" element={<HomepageLayout />}>
                <Route index element={<Homepage homepageState={'signUp'} />} />
            </Route>
            <Route path="/confirm">
                <Route index element={<Homepage homepageState={'confirmSignUp'} />} />
            </Route>
            <Route path="/signout">
                <Route index element={<SignOut />} />
            </Route>

            <Route element={<BlockAccessIfNotSignedIn />}>
                <Route path="/initial-login" element={<InitialLoginLayout />}>
                    <Route index element={<InitialLoginPage />} />
                </Route>

                <Route path="/admin" element={<AdminLayout />}>
                    <Route path="dashboard" element={<AdminDashboardPage />} />
                    <Route path="notify-users" element={<NotifyUsersPage />} />
                    <Route path="add-user" element={<AddUserPage />} />
                    <Route path="change-user-role/:id" element={<ChangeUserRolePage />} />
                    <Route path="create-organisation" element={<CreateOrganisationPage />} />
                    <Route path="organisation/:id" element={<UpdateOrganisationPage />} />
                </Route>
                <Route path="/invitations" element={<MainLayout />}>
                    <Route path=":id" element={<InvitationPage />} />
                </Route>

                <Route path="/build-course/:courseId" element={<CourseBuilderLayout editMode={false} />}>
                    <Route index element={<CourseBuilderPage />} />
                </Route>

                <Route path="/preview/:courseId" element={<PreviewLayout />}>
                    <Route index element={<LearnerCoursePage />} />
                </Route>

                <Route path="/published-course/:courseId" element={<OrganisationOrTrainerLayout />}>
                    <Route index element={<ViewPublishedCoursePage />} />
                    <Route path="schedule" element={<ScheduleCoursePage />} />
                </Route>

                <Route element={<CourseBuilderLayout editMode />}>
                    <Route path="/published-course/:courseId/edit" element={<CourseBuilderPage />} />
                </Route>

                <Route path="/trainer" element={<OrganisationOrTrainerLayout />}>
                    <Route path="dashboard" element={<TrainerDashboardPage />} />
                </Route>

                <Route path="/live-course/:courseId" element={<MainLayout />}>
                    <Route index element={<LiveCourseDetailPage />} />
                    <Route path="live-training/:liveTrainingId" element={<LiveTrainingDetailPage />} />
                </Route>

                <Route path="/organisation" element={<OrganisationLayout />}>
                    <Route path="dashboard" element={<OADashboard />}>
                        <Route index element={<OADashboardContent />} />
                        <Route path="users/:id" element={<UserDetailsPage />} />
                    </Route>
                    <Route path="overview" element={<OrganisationOverviewPage />} />
                    <Route path="files/upload" element={<FileUploadPage />} />
                    <Route path="logo" element={<LogoUploadPage />} />
                </Route>

                <Route path="/learner" element={<LearnerLayout />}>
                    <Route path="courses" element={<LearnerDashboard />} />
                    <Route path="enroll/:courseScheduleId" element={<EnrollPage />} />
                </Route>

                <Route path="/learner/courses/:enrollmentId" element={<LearnerCourseLayout />}>
                    <Route index element={<LearnerCoursePage />} />
                </Route>

                <Route path="/profile" element={<ProfileLayout />}>
                    <Route index element={<ProfilePage />} />
                </Route>

                <Route path={'/notifications'} element={<MainLayout />}>
                    <Route index element={<NotificationsPage />} />
                </Route>
            </Route>
            <Route path="*" element={<Redirect />} />
        </>,
    ),
);

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <AuthProvider>
        <GlobalStyle />
        <RouterProvider router={router} />
    </AuthProvider>,
);
